import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Info, Trash2 } from 'lucide-react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DataTableFilters } from '@/components/data-table/filters-v1/data-table-filters';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip';
import { displayToastError } from '@/helpers';
import { useDataTable } from '@/hooks/use-data-table';
import { useFilterQuery } from '@/hooks/use-filter-query';
import { useModal } from '@/hooks/use-modal';
import { ConfirmationModal } from '@/modules/common/components/modals/confirmation-modal';
import { BottomSectionProps } from '@/modules/common/components/templates/data-reconciliation/components/parts/data-reconciliation-bottom-section';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import { EntityChangesManager } from '@/modules/common/utils/entity-changes-manager';
import {
  EntitiesFilterFieldsKeys,
  getEntitiesOnboardingFiltersConfig,
} from '@/modules/onboarding/common/filters/entities-onboarding-filters-config';
import {
  UiEntitiesFilter,
  useEntitiesQueryFilters,
} from '@/modules/onboarding/common/hooks/use-entities-query-filters';
import { usePaginatedEntities } from '@/modules/onboarding/common/hooks/use-paginated-entities';
import { EntitiesSubmitErrorCode } from '@/modules/onboarding/common/types/entity-validation-error.response';
import { ONBOARDING_QUERY_STALE_TIME } from '@/modules/onboarding/common/utils/onboarding-query-invalidation';
import { logger } from '@/utils/logger/logger';

import { ConfirmDeleteEntityModal } from '../../onboarding/common/components/confirm-delete-entity-modal';
import { EntityOnboardingTable } from '../../onboarding/common/components/entity-onboarding-table';
import {
  mergeFilteredEntities,
  useClientSideValidationReport,
} from '../../onboarding/common/hooks/use-client-side-validation-report';
import { BaseOnboardingEntity } from '../../onboarding/common/types/base-onboarding-entity';
import { EntityReconciliationConfig } from '../../onboarding/common/types/entity-reconciliation-config';

export interface EntityReconciliationStepProps<
  TEntity extends BaseOnboardingEntity,
  TFormValues extends FieldValues,
  TChangeValues extends Record<string, any> = Record<string, any>,
> {
  config: EntityReconciliationConfig<TEntity, TFormValues, TChangeValues>;
  isInfiniteSizeTable?: boolean;
  onClickNext: () => void;
  bottomSection: (props: BottomSectionProps) => React.ReactNode;
  onDirtyChange?: (isDirty: boolean) => void;
}

const DEFAULT_PERMISSIONS = { canCreate: true, canEdit: true, canDelete: true };

function areEntityIdsEqual<TEntity extends BaseOnboardingEntity>(
  current: TEntity[],
  next: TEntity[]
): boolean {
  if (current.length !== next.length) return false;

  return current.every((entity, index) => entity.id === next[index]?.id);
}

function areIdSetsEqual(current: Set<number> | undefined, next: Set<number>): boolean {
  if (!current || current.size !== next.size) return false;

  for (const entityId of next) {
    if (!current.has(entityId)) {
      return false;
    }
  }

  return true;
}

function removeEntityIdsFromIssueMap(
  current: Partial<Record<UiEntitiesFilter, Set<number>>>,
  entityIds: number[]
): Partial<Record<UiEntitiesFilter, Set<number>>> {
  const entityIdSet = new Set(entityIds);

  return {
    [UiEntitiesFilter.DUPLICATES]: new Set(
      Array.from(current[UiEntitiesFilter.DUPLICATES] ?? []).filter(id => !entityIdSet.has(id))
    ),
    [UiEntitiesFilter.ERRORS]: new Set(
      Array.from(current[UiEntitiesFilter.ERRORS] ?? []).filter(id => !entityIdSet.has(id))
    ),
    [UiEntitiesFilter.WARNINGS]: new Set(
      Array.from(current[UiEntitiesFilter.WARNINGS] ?? []).filter(id => !entityIdSet.has(id))
    ),
  };
}

export function EntityReconciliationStep<
  TEntity extends BaseOnboardingEntity,
  TFormValues extends FieldValues,
  TChangeValues extends Record<string, any> = Record<string, any>,
>({
  config,
  onClickNext,
  bottomSection,
  isInfiniteSizeTable,
  onDirtyChange,
}: EntityReconciliationStepProps<TEntity, TFormValues, TChangeValues>) {
  const TAG = `${config.entityKey}ReconciliationStep:`;
  const { t } = useTranslation(config.i18nNamespaces);

  const [selectedRows, setSelectedRows] = useState<TEntity[]>([]);
  const [knownIssueEntityIds, setKnownIssueEntityIds] = useState<
    Partial<Record<UiEntitiesFilter, Set<number>>>
  >({});

  const { uiEntitiesFilter, searchString } = useEntitiesQueryFilters({
    selectedCount: selectedRows.length,
  });
  const { set: setFilterParam } = useFilterQuery();
  const { displayToastErrorFromApiWithFallback } = useApiError();
  const queryClient = useQueryClient();

  const [changesManager] = useState(() => new EntityChangesManager(config.fieldMapper));
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Use ref for config to avoid unstable object references in dependency arrays.
  const configRef = useRef(config);
  configRef.current = config;

  // Optional hooks (must be called unconditionally for rules of hooks)
  const extraColumnDeps = config.useExtraColumnDeps?.();

  const permissions = config.usePermissionChecks?.() ?? DEFAULT_PERMISSIONS;

  // --- Master entity list ---
  // The canonical list of ALL unfiltered entities. Updated only from unfiltered backend responses.
  // Used as the baseline for: form values, change detection, validation, formIndexMap.
  const masterEntityListRef = useRef<TEntity[]>([]);

  // --- Form index lookup ---
  // Entity id → index in the FULL form array. The table renders a filtered subset, so `row.index`
  // is NOT the form index. Declared here, populated further down (after the master list is
  // updated), and handed to the column factory as an explicit dependency: cells call it while
  // rendering, i.e. after this render pass has filled the map.
  const formIndexMapRef = useRef(new Map<number, number>());
  const getFormIndex = useCallback(
    (entityId: number) => formIndexMapRef.current.get(entityId) ?? 0,
    []
  );

  // True while we are programmatically writing form values (applyFormFromEntities, delete cleanup).
  // Suppresses the form.watch change-detection callback to avoid false dirty tracking.
  const isWritingFormRef = useRef(false);
  const validationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Tracks whether any input inside the table is currently focused.
  // Used to suppress auto-navigation to "All" while the user is mid-edit.
  const isTableFocusedRef = useRef(false);

  // Snapshot of entity IDs that were in the filtered list when the user entered
  // the current filter tab. Keeps rows visible while the user is still editing them.
  const pinnedFilterEntityIdsRef = useRef<Set<number>>(new Set());

  // Tracks which filter tab the current pinned set belongs to.
  const pinnedFilterTabRef = useRef<UiEntitiesFilter | null>(null);

  // Tombstone set: IDs explicitly removed by delete/merge mutations.
  // Prevents stale filtered-tab backend responses from re-adding ghost entities
  // to the master list after a mutation removes them.
  const removedEntityIdsRef = useRef<Set<number>>(new Set());

  // --- Data fetching ---

  const { data: validationReport } = useQuery({
    queryKey: [config.queryKeys.validationReport],
    queryFn: config.getValidationReport,
    staleTime: ONBOARDING_QUERY_STALE_TIME,
    initialData: {
      total: 0,
      duplicatesCount: 0,
      errorsCount: 0,
      warningsCount: 0,
    },
    initialDataUpdatedAt: 0,
  });

  const {
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    error,
    hasNextPage,
    entities: backendEntities,
    total: totalEntities,
    refetchAfterMutation,
    refetchAfterAddition,
  } = usePaginatedEntities({
    queryKey: config.queryKeys.entities,
    getEntities: config.getEntities,
    uiEntitiesFilter,
    searchString,
    selectedRows,
  });

  // --- Form setup ---

  const schema = useMemo(() => configRef.current.getSchema(t), [t]);

  const form = useForm<TFormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: config.defaultFormValues as any,
  });

  const columns = useMemo(
    () => configRef.current.getColumns({ t, form, getFormIndex, extraDeps: extraColumnDeps }),
    [t, form, getFormIndex, extraColumnDeps]
  );

  // --- Core: write form values from entities + tracked changes ---
  // This is the ONLY function that writes entity data into the form.
  // Builds the complete form array atomically: for each entity, takes the original
  // field values and overlays any tracked changes. Then sets the entire array at once
  // via setValue(prefix, array) to guarantee the form array length matches the entities
  // (no stale trailing entries from a previous longer list).
  const applyFormFromEntities = useCallback(
    (entitiesToApply: TEntity[]) => {
      const prefix = configRef.current.formFieldPrefix;
      const fieldMapper = configRef.current.fieldMapper;

      logger.debug(TAG, 'applyFormFromEntities', {
        count: entitiesToApply.length,
        changedCount: changesManager.getChangedEntitiesCount(),
        changedIds: changesManager.getChangedEntityIds(),
      });

      // Build the complete array: original values + tracked change overlays.
      // Normalize null/undefined to empty strings so the form always holds strings
      // (fieldMapper.getTrackableFields may return null for optional fields like email,
      // but the Zod schema expects string values — null would fail validation).
      const normalizeFields = (fields: Record<string, unknown>): Record<string, string> => {
        const result: Record<string, string> = {};
        for (const [key, value] of Object.entries(fields)) {
          result[key] = value == null ? '' : String(value);
        }
        return result;
      };

      const formArray = entitiesToApply.map(entity => {
        const baseFields = normalizeFields(
          fieldMapper.getTrackableFields(entity) as Record<string, unknown>
        );
        const entityChanges = changesManager.getChanges(entity.id);

        if (entityChanges) {
          // Overlay tracked changes on top of original values
          return { ...baseFields, ...normalizeFields(entityChanges as Record<string, unknown>) };
        }
        return baseFields;
      });

      // Set the entire array atomically — this guarantees:
      // 1. Form array length === entities length (no stale trailing entries)
      // 2. All field values are set in one batch (no partial updates)
      isWritingFormRef.current = true;
      form.setValue(prefix as any, formArray as any);
      isWritingFormRef.current = false;
    },
    [changesManager, form, TAG]
  );

  const scheduleFormValidation = useCallback(() => {
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }

    validationTimeoutRef.current = setTimeout(() => {
      void form.trigger();
      validationTimeoutRef.current = null;
    }, 0);
  }, [form]);

  // Signals that the next backend data arrival should be handled by the force-apply path
  // (after merge or bulk update) which cleans up orphaned/stale changes before applying.
  const pendingForceApplyRef = useRef(false);

  // Tracks which backendEntities have already been applied to the form to avoid re-applying.
  const lastAppliedEntitiesRef = useRef<TEntity[]>([]);
  const lastAppliedBackendEntitiesRef = useRef<TEntity[]>([]);

  // --- Sync backend entities → master list (during render) ---
  // Update masterEntityListRef synchronously during render so formIndexMapRef
  // is correct on the same render cycle. Without this, the first render would
  // have an empty master list and every row would resolve to form index 0.
  // Skip when a force-apply is pending — the mutation handler already set the
  // correct local master list, and the cached backendEntities may still be stale.
  const isUnfilteredView = !uiEntitiesFilter && !searchString;
  const visibleBackendEntities =
    removedEntityIdsRef.current.size > 0
      ? backendEntities.filter(entity => !removedEntityIdsRef.current.has(entity.id))
      : backendEntities;

  if (isUnfilteredView && visibleBackendEntities.length > 0 && !pendingForceApplyRef.current) {
    // Merge: keep extras from the current master list (e.g. entities loaded via search
    // that are not yet in the current unfiltered page) so their form entries and tracked
    // changes are preserved.
    const backendEntityIds = new Set(visibleBackendEntities.map(entity => entity.id));
    const extraEntities = masterEntityListRef.current.filter(
      entity => !removedEntityIdsRef.current.has(entity.id) && !backendEntityIds.has(entity.id)
    );
    masterEntityListRef.current =
      extraEntities.length > 0
        ? [...visibleBackendEntities, ...extraEntities]
        : visibleBackendEntities;
  } else if (visibleBackendEntities.length > 0) {
    const knownEntityIds = new Set(masterEntityListRef.current.map(entity => entity.id));
    const missingEntities = visibleBackendEntities.filter(
      entity => !knownEntityIds.has(entity.id) && !removedEntityIdsRef.current.has(entity.id)
    );

    if (missingEntities.length > 0) {
      masterEntityListRef.current = [...masterEntityListRef.current, ...missingEntities];
    }
  }

  // --- Sync backend entities → form (in effect) ---
  // Writes form values after render. Captures pending edits first, then overwrites
  // with backend data + tracked changes overlay.
  useEffect(() => {
    if (!isUnfilteredView || visibleBackendEntities.length === 0) return;
    // Skip if we already applied this exact entity array reference
    if (backendEntities === lastAppliedBackendEntitiesRef.current) return;
    // Skip if a force-apply is pending (merge/bulk update) — let the force-apply
    // effect handle this data arrival so it can clean up orphaned changes first.
    if (pendingForceApplyRef.current) return;

    // Before overwriting, capture any pending edits from the current form
    const currentFormValues = form.getValues(configRef.current.formFieldPrefix as any) as any[];
    const prevMasterLen = lastAppliedEntitiesRef.current.length;

    if (prevMasterLen > 0 && currentFormValues?.length === prevMasterLen) {
      logger.debug(TAG, 'detectChangesFromForm before form sync', {
        formLen: currentFormValues.length,
        prevMasterLen,
        changedBefore: changesManager.getChangedEntitiesCount(),
      });
      changesManager.detectChangesFromForm(lastAppliedEntitiesRef.current, currentFormValues);
      logger.debug(TAG, 'detectChangesFromForm result', {
        changedAfter: changesManager.getChangedEntitiesCount(),
        changedIds: changesManager.getChangedEntityIds(),
      });
    }

    lastAppliedEntitiesRef.current = masterEntityListRef.current;
    lastAppliedBackendEntitiesRef.current = backendEntities;

    // Write form values from the master list + tracked changes.
    // Use masterEntityListRef.current (not backendEntities) so that entities loaded
    // via search but not yet in the current unfiltered page retain their form entries
    // and any tracked changes / validation errors.
    applyFormFromEntities(masterEntityListRef.current);
    scheduleFormValidation();

    // Notify parent about dirty state
    onDirtyChange?.(changesManager.getChangedEntitiesCount() > 0);

    logger.debug(TAG, 'form synced from backend', {
      masterLen: masterEntityListRef.current.length,
      changedCount: changesManager.getChangedEntitiesCount(),
    });
  }, [
    backendEntities,
    visibleBackendEntities,
    isUnfilteredView,
    changesManager,
    form,
    applyFormFromEntities,
    scheduleFormValidation,
    onDirtyChange,
    TAG,
  ]);

  useEffect(() => {
    if (isUnfilteredView || visibleBackendEntities.length === 0 || pendingForceApplyRef.current)
      return;

    const knownEntityIds = new Set(lastAppliedEntitiesRef.current.map(entity => entity.id));
    // Exclude tombstoned IDs: entities explicitly removed by delete/merge mutations.
    // Stale filtered-tab cache responses (e.g. DUPLICATES filter still in flight when
    // the merge completes) can contain the removed entities; without this guard they
    // would be re-added to the master list as phantom rows.
    const missingEntities = visibleBackendEntities.filter(
      entity => !knownEntityIds.has(entity.id) && !removedEntityIdsRef.current.has(entity.id)
    );

    if (missingEntities.length === 0) return;

    const nextEntities = [...lastAppliedEntitiesRef.current, ...missingEntities];

    lastAppliedEntitiesRef.current = nextEntities;
    applyFormFromEntities(nextEntities);
    scheduleFormValidation();

    logger.debug(TAG, 'synced searched entities into form', {
      addedEntityIds: missingEntities.map(entity => entity.id),
      masterLen: nextEntities.length,
      backendEntitiesLen: visibleBackendEntities.length,
    });
  }, [
    backendEntities,
    visibleBackendEntities,
    isUnfilteredView,
    applyFormFromEntities,
    scheduleFormValidation,
    TAG,
  ]);

  // --- Force-apply effect ---
  // After merge/bulk update, invalidation causes backendEntities to change.
  // Cleans up orphaned changes (IDs no longer in data) before applying.
  useEffect(() => {
    if (!pendingForceApplyRef.current || visibleBackendEntities.length === 0) return;
    pendingForceApplyRef.current = false;

    // Remove tracked changes for entity IDs that no longer exist in fresh data
    const freshEntityIds = new Set(visibleBackendEntities.map(e => e.id));
    const orphanedIds = changesManager.getChangedEntityIds().filter(id => !freshEntityIds.has(id));
    if (orphanedIds.length > 0) {
      changesManager.clearEntitiesChanges(orphanedIds);
      logger.debug(TAG, 'force-apply cleared orphaned changes', { orphanedIds });
    }

    masterEntityListRef.current = visibleBackendEntities;
    lastAppliedEntitiesRef.current = visibleBackendEntities;
    lastAppliedBackendEntitiesRef.current = backendEntities;
    applyFormFromEntities(visibleBackendEntities);
    scheduleFormValidation();
    onDirtyChange?.(changesManager.getChangedEntitiesCount() > 0);

    logger.debug(TAG, 'force-applied form after mutation', {
      masterLen: backendEntities.length,
      visibleMasterLen: visibleBackendEntities.length,
      changedCount: changesManager.getChangedEntitiesCount(),
      changedIds: changesManager.getChangedEntityIds(),
    });
  }, [
    backendEntities,
    visibleBackendEntities,
    applyFormFromEntities,
    changesManager,
    scheduleFormValidation,
    onDirtyChange,
    TAG,
  ]);

  // --- User edit tracking ---
  // Debounced form.watch subscription detects user edits and records them in changesManager.
  // Also triggers full-schema validation for cross-entity duplicate detection.
  const triggerTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const subscription = form.watch(() => {
      // Skip when we're programmatically writing form values
      if (isWritingFormRef.current) return;

      if (triggerTimeoutRef.current) clearTimeout(triggerTimeoutRef.current);
      triggerTimeoutRef.current = setTimeout(() => {
        // Detect changes: compare current form values against master entity list
        const prefix = configRef.current.formFieldPrefix;
        const currentFormValues = form.getValues(prefix as any) as any[];
        const masterEntities = masterEntityListRef.current;

        if (masterEntities.length > 0 && currentFormValues?.length === masterEntities.length) {
          changesManager.detectChangesFromForm(masterEntities, currentFormValues);
          onDirtyChange?.(changesManager.getChangedEntitiesCount() > 0);

          logger.debug(TAG, 'user edit detected', {
            changedCount: changesManager.getChangedEntitiesCount(),
            changedIds: changesManager.getChangedEntityIds(),
          });
        } else if (masterEntities.length > 0) {
          logger.debug(TAG, 'user edit skipped — length mismatch', {
            formLen: currentFormValues?.length,
            masterLen: masterEntities.length,
          });
        }

        // Trigger full schema validation for cross-entity duplicate detection
        form.trigger();
      }, 200);
    });
    return () => {
      subscription.unsubscribe();
      if (triggerTimeoutRef.current) clearTimeout(triggerTimeoutRef.current);
      if (validationTimeoutRef.current) clearTimeout(validationTimeoutRef.current);
    };
  }, [form, changesManager, onDirtyChange, TAG]);

  // Watch form field array values for real-time duplicate detection and error counting.
  const watchedFieldValues = form.watch(configRef.current.formFieldPrefix as any);
  const formErrors = form.formState.errors;

  // --- Validation ---
  const clientValidation = useClientSideValidationReport({
    backendReport: validationReport,
    formErrors,
    formFieldPrefix: configRef.current.formFieldPrefix,
    formFieldValues: watchedFieldValues ?? [],
    duplicateFieldExtractors: configRef.current.duplicateFieldExtractors,
    entities: masterEntityListRef.current,
    knownIssueEntityIds,
    connectedIntegrationsCount: config.integrationColumnsCount,
  });

  useEffect(() => {
    const loadedEntityIds = new Set(masterEntityListRef.current.map(entity => entity.id));

    const nextKnownIssueEntityIds: Partial<Record<UiEntitiesFilter, Set<number>>> = {
      [UiEntitiesFilter.DUPLICATES]: new Set([
        ...(knownIssueEntityIds[UiEntitiesFilter.DUPLICATES] ?? []),
        ...clientValidation.duplicateEntityIds,
      ]),
      [UiEntitiesFilter.ERRORS]: new Set([
        ...(knownIssueEntityIds[UiEntitiesFilter.ERRORS] ?? []),
        ...clientValidation.errorEntityIds,
      ]),
      [UiEntitiesFilter.WARNINGS]: new Set([
        ...(knownIssueEntityIds[UiEntitiesFilter.WARNINGS] ?? []),
        ...clientValidation.warningEntityIds,
      ]),
    };

    Object.values(UiEntitiesFilter).forEach(filter => {
      if (filter === UiEntitiesFilter.SELECTED) {
        return;
      }

      const ids = nextKnownIssueEntityIds[filter];
      if (!ids) {
        return;
      }

      nextKnownIssueEntityIds[filter] = new Set(
        Array.from(ids).filter(entityId => loadedEntityIds.has(entityId))
      );
    });

    const hasChanges =
      !areIdSetsEqual(
        knownIssueEntityIds[UiEntitiesFilter.DUPLICATES],
        nextKnownIssueEntityIds[UiEntitiesFilter.DUPLICATES] ?? new Set()
      ) ||
      !areIdSetsEqual(
        knownIssueEntityIds[UiEntitiesFilter.ERRORS],
        nextKnownIssueEntityIds[UiEntitiesFilter.ERRORS] ?? new Set()
      ) ||
      !areIdSetsEqual(
        knownIssueEntityIds[UiEntitiesFilter.WARNINGS],
        nextKnownIssueEntityIds[UiEntitiesFilter.WARNINGS] ?? new Set()
      );

    if (!hasChanges) {
      return;
    }

    setKnownIssueEntityIds(nextKnownIssueEntityIds);
  }, [
    clientValidation.duplicateEntityIds,
    clientValidation.errorEntityIds,
    clientValidation.warningEntityIds,
    knownIssueEntityIds,
  ]);

  // --- Filter tab change logging ---
  useEffect(() => {
    logger.debug(TAG, 'filter tab changed', {
      filter: uiEntitiesFilter ?? 'ALL',
      searchString: searchString ?? '',
      masterLen: masterEntityListRef.current.length,
      backendEntitiesLen: backendEntities.length,
      formLen: (form.getValues(configRef.current.formFieldPrefix as any) as any[])?.length,
      changedCount: changesManager.getChangedEntitiesCount(),
      changedIds: changesManager.getChangedEntityIds(),
      selectedCount: selectedRows.length,
      validation: {
        errors: clientValidation.errorEntityIds.size,
        duplicates: clientValidation.duplicateEntityIds.size,
        warnings: clientValidation.warningEntityIds.size,
        errorIds: Array.from(clientValidation.errorEntityIds),
        duplicateIds: Array.from(clientValidation.duplicateEntityIds),
        warningIds: Array.from(clientValidation.warningEntityIds),
      },
      backendReport: validationReport,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiEntitiesFilter, searchString]);

  // --- Pinned filter entity IDs ---
  // When entering a filtered tab, snapshot matching entity IDs so rows
  // don't disappear while the user is editing them.
  useEffect(() => {
    const FILTERABLE_TABS = [
      UiEntitiesFilter.ERRORS,
      UiEntitiesFilter.DUPLICATES,
      UiEntitiesFilter.WARNINGS,
    ];

    if (!uiEntitiesFilter || !FILTERABLE_TABS.includes(uiEntitiesFilter)) {
      pinnedFilterEntityIdsRef.current = new Set();
      pinnedFilterTabRef.current = null;
      return;
    }

    // Tab changed — start a fresh pinned set for the new tab.
    if (pinnedFilterTabRef.current !== uiEntitiesFilter) {
      pinnedFilterEntityIdsRef.current = new Set();
      pinnedFilterTabRef.current = uiEntitiesFilter;
    }

    let currentIds: Set<number> = new Set();
    switch (uiEntitiesFilter) {
      case UiEntitiesFilter.ERRORS:
        currentIds = clientValidation.errorEntityIds;
        break;
      case UiEntitiesFilter.DUPLICATES:
        currentIds = clientValidation.duplicateEntityIds;
        break;
      case UiEntitiesFilter.WARNINGS:
        currentIds = clientValidation.warningEntityIds;
        break;
    }

    // Only expand the pinned set — never shrink it mid-session.
    pinnedFilterEntityIdsRef.current = new Set([
      ...pinnedFilterEntityIdsRef.current,
      ...currentIds,
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiEntitiesFilter]);

  // --- Auto-navigate to "All" when filter count reaches zero ---
  useEffect(() => {
    if (
      uiEntitiesFilter === UiEntitiesFilter.ERRORS &&
      (clientValidation.report?.errorsCount ?? 0) === 0 &&
      !isTableFocusedRef.current
    ) {
      setFilterParam(EntitiesFilterFieldsKeys.PRESET, '');
    }
  }, [clientValidation.report?.errorsCount, uiEntitiesFilter, setFilterParam]);

  useEffect(() => {
    if (
      uiEntitiesFilter === UiEntitiesFilter.DUPLICATES &&
      (clientValidation.report?.duplicatesCount ?? 0) === 0 &&
      !isTableFocusedRef.current
    ) {
      logger.warn(TAG, 'AUTO-NAVIGATING to All from DUPLICATES');
      setFilterParam(EntitiesFilterFieldsKeys.PRESET, '');
    }
  }, [clientValidation.report?.duplicatesCount, uiEntitiesFilter, setFilterParam]);

  useEffect(() => {
    if (
      uiEntitiesFilter === UiEntitiesFilter.WARNINGS &&
      (clientValidation.report?.warningsCount ?? 0) === 0 &&
      !isTableFocusedRef.current
    ) {
      logger.warn(TAG, 'AUTO-NAVIGATING to All from WARNINGS');
      setFilterParam(EntitiesFilterFieldsKeys.PRESET, '');
    }
  }, [clientValidation.report?.warningsCount, uiEntitiesFilter, setFilterParam]);

  // --- Merge filtered entities for display ---
  const entities = useMemo(
    () =>
      mergeFilteredEntities(
        visibleBackendEntities,
        masterEntityListRef.current,
        uiEntitiesFilter,
        clientValidation,
        pinnedFilterEntityIdsRef.current
      ),
    [visibleBackendEntities, uiEntitiesFilter, clientValidation]
  );

  // --- Form index map ---
  // Fills the map declared above, from masterEntityList (always complete). Must run AFTER the
  // synchronous master-list update above, so the indexes match what the cells will read.
  if (masterEntityListRef.current.length > 0) {
    const masterMap = new Map<number, number>();
    masterEntityListRef.current.forEach((entity, index) => masterMap.set(entity.id, index));
    formIndexMapRef.current = masterMap;
  }

  const { table, saveColumnSizing } = useDataTable({
    data: entities,
    columns,
    enableRowSelection: true,
    getRowId: (row: TEntity) => String(row.id),
    tableId: config.tableId,
  });

  // Stable callback to prevent IntersectionObserver from reconnecting on every render.
  const fetchNextPageRef = useRef(fetchNextPage);
  fetchNextPageRef.current = fetchNextPage;
  const handleLoadMore = useCallback(() => {
    fetchNextPageRef.current();
  }, []);

  // Derive selected rows from rowSelection state + master entity list.
  // table.getSelectedRowModel() only returns rows present in the current filtered view,
  // so switching filters (e.g. from "Selected" to "Errors") would lose track of selections
  // that aren't in the current filter. Using rowSelection + masterEntityList preserves
  // the full selection across filter switches.
  const rowSelection = table.getState().rowSelection;
  useEffect(() => {
    const selectedIds = new Set(
      Object.entries(rowSelection)
        .filter(([, selected]) => selected)
        .map(([id]) => Number(id))
    );
    if (selectedIds.size === 0) {
      setSelectedRows(current => (current.length === 0 ? current : []));
      return;
    }

    const selected = masterEntityListRef.current.filter(entity => selectedIds.has(entity.id));

    setSelectedRows(current => (areEntityIdsEqual(current, selected) ? current : selected));
  }, [rowSelection]);

  // Derive selected rows enriched with current form values.
  // form.watch() returns a mutable reference whose identity never changes, so we
  // serialise only the slices relevant to the selected rows as a cache-busting key.
  // This is cheap (only 2-3 selected rows) and runs on every render, but the
  // useMemo below avoids creating new objects unless the values actually changed.
  const selectedFormSlice =
    selectedRows.length === 0
      ? ''
      : JSON.stringify(
          selectedRows.map(entity => {
            const formIndex = formIndexMapRef.current.get(entity.id);
            return formIndex !== undefined ? watchedFieldValues?.[formIndex] : null;
          })
        );

  const selectedRowsWithFormValues = useMemo(
    () =>
      selectedRows.map(entity => {
        const formIndex = formIndexMapRef.current.get(entity.id);
        if (formIndex === undefined) return entity;

        const formValue = watchedFieldValues?.[formIndex];
        if (!formValue || typeof formValue !== 'object') return entity;

        return {
          ...entity,
          ...(formValue as Partial<TEntity>),
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedRows, selectedFormSlice]
  );

  const deleteModalProps = useModal();
  const warningsModalProps = useModal();

  // --- Delete mutation ---
  const deleteMutation = useMutation({
    mutationFn: () => {
      if (selectedRows.length === 0) return Promise.resolve();
      const selectedIds = selectedRows.map(row => row.id);
      return config.deleteEntities(selectedIds);
    },
    onSuccess: async () => {
      const selectedIds = selectedRows.map(row => row.id);
      const selectedIdSet = new Set(selectedIds);
      const loadedCountBeforeDelete = masterEntityListRef.current.length;

      logger.debug(TAG, 'delete onSuccess', {
        deletedIds: selectedIds,
        masterLenBefore: loadedCountBeforeDelete,
        changedCountBefore: changesManager.getChangedEntitiesCount(),
      });

      // 1. Clear tracked changes for deleted entities
      changesManager.clearEntitiesChanges(selectedIds);
      setKnownIssueEntityIds(current => removeEntityIdsFromIssueMap(current, selectedIds));

      // 2. Remove from pinned set; add to tombstone to block ghost re-additions
      selectedIds.forEach(id => {
        pinnedFilterEntityIdsRef.current.delete(id);
        removedEntityIdsRef.current.add(id);
      });

      // 3. Update master entity list (remove deleted entities)
      const remainingEntities = masterEntityListRef.current.filter(e => !selectedIdSet.has(e.id));
      masterEntityListRef.current = remainingEntities;
      lastAppliedEntitiesRef.current = remainingEntities;
      lastAppliedBackendEntitiesRef.current = [];

      // 4. Rebuild form from remaining entities + surviving changes.
      // applyFormFromEntities sets the entire array atomically, so no form.reset needed.
      applyFormFromEntities(remainingEntities);
      scheduleFormValidation();

      logger.debug(TAG, 'delete form rebuilt', {
        masterLenAfter: remainingEntities.length,
        changedCountAfter: changesManager.getChangedEntitiesCount(),
        changedIds: changesManager.getChangedEntityIds(),
      });

      // 5. Notify dirty state
      onDirtyChange?.(changesManager.getChangedEntitiesCount() > 0);

      // 6. Invalidate validation report
      await queryClient.invalidateQueries({ queryKey: [config.queryKeys.validationReport] });

      // 7. Refetch entities in background with correct page size
      void refetchAfterMutation(loadedCountBeforeDelete, selectedIds.length, selectedIds);

      // 8. Clean up UI state
      deleteModalProps.onClose();
      table.resetRowSelection();
    },
    onError: (error: unknown) => {
      if (isMountedRef.current) {
        displayToastErrorFromApiWithFallback(error, config.i18nKeys.deleteErrorFallback);
      }
    },
  });

  // --- Update (bulk save) mutation ---
  const updateMutation = useMutation({
    mutationFn: async (updateRequest: { items: any[] }) => {
      if (updateRequest.items.length > 0) {
        await config.updateEntitiesBulk(updateRequest);
        changesManager.clearAllChanges();
        // Signal that the next backend data arrival should force-apply to the form
        // even if a filter tab is active, so client-side validation clears.
        pendingForceApplyRef.current = true;
        await config.invalidateQueries(queryClient);
      }
    },
    onError: (error: unknown) => {
      if (isMountedRef.current) {
        displayToastErrorFromApiWithFallback(error, config.i18nKeys.updateErrorFallback);
      }
    },
  });

  // --- Submit mutation ---
  const submitMutation = useMutation({
    mutationFn: async () => {
      const response = await config.submitEntities();
      switch (response.errorCode) {
        case EntitiesSubmitErrorCode.ENTITIES_WITH_ERRORS_EXIST:
          throw new Error(t(config.i18nKeys.submitEntitiesWithErrorsExist));
        case EntitiesSubmitErrorCode.DUPLICATE_ENTITIES_EXISTS:
          setFilterParam(EntitiesFilterFieldsKeys.PRESET, UiEntitiesFilter.DUPLICATES);
          throw new Error(t(config.i18nKeys.submitDuplicateEntitiesExists));
        default:
          return;
      }
    },
    onSuccess: () => {
      if (isMountedRef.current) {
        onClickNext();
      }
    },
    onError: (error: unknown) => {
      if (isMountedRef.current) {
        if (error instanceof Error) {
          displayToastError(error.message);
        } else {
          displayToastErrorFromApiWithFallback(error, 'common:api.error.generic');
        }
      }
    },
  });

  // --- Submit flow ---
  const submitIgnoringWarnings = async () => {
    const allChanges = changesManager.getAllChanges();
    const changedItems: any[] = [];
    const fieldMapper = configRef.current.fieldMapper;

    allChanges.forEach((changes, entityId) => {
      const entity = masterEntityListRef.current.find(
        currentEntity => currentEntity.id === entityId
      );

      if (!entity) {
        return;
      }

      const fullFieldValues = {
        ...fieldMapper.getTrackableFields(entity),
        ...changes,
      };

      changedItems.push(config.buildUpdateRequestItem(entityId, fullFieldValues as TChangeValues));
    });

    logger.debug(TAG, 'submitting changed entities', {
      count: changedItems.length,
      entityIds: Array.from(allChanges.keys()),
    });

    if (changedItems.length > 0) {
      await updateMutation.mutateAsync({ items: changedItems });
    }

    submitMutation.mutate();
  };

  const onSubmitValid = () => {
    if (clientValidation.report?.warningsCount && clientValidation.report.warningsCount > 0) {
      warningsModalProps.onOpen();
      return;
    }
    submitIgnoringWarnings();
  };

  // --- Merge success handler ---
  // Follows the same pattern as delete: locally remove affected entities,
  // rebuild form, strip them from cache, refetch in background.
  // Does NOT use pendingForceApplyRef — like delete, local state is the
  // source of truth. The new merged entity appears when the background
  // refetch completes via the normal sync effect.
  const handleMergeSuccess = useCallback(() => {
    const mergedIds = selectedRows.map(row => row.id);
    const mergedIdSet = new Set(mergedIds);
    const loadedCountBefore = masterEntityListRef.current.length;
    const removedCount = selectedRows.length - 1;

    logger.debug(TAG, 'merge success', {
      loadedCountBefore,
      removedCount,
      mergedIds,
      changedCountBefore: changesManager.getChangedEntitiesCount(),
      changedIdsBefore: changesManager.getChangedEntityIds(),
    });

    // 1. Clear tracked changes for merged entities
    changesManager.clearEntitiesChanges(mergedIds);
    setKnownIssueEntityIds(current => removeEntityIdsFromIssueMap(current, mergedIds));

    // 2. Remove from pinned set; add to tombstone to block ghost re-additions.
    // Both source IDs are tombstoned. The winner will re-enter master via the
    // fresh backend refetch with its updated data; the loser is permanently gone.
    mergedIds.forEach(id => {
      pinnedFilterEntityIdsRef.current.delete(id);
      removedEntityIdsRef.current.add(id);
    });

    // 3. Update master entity list (remove merged entities)
    const remainingEntities = masterEntityListRef.current.filter(e => !mergedIdSet.has(e.id));
    masterEntityListRef.current = remainingEntities;
    lastAppliedEntitiesRef.current = remainingEntities;
    lastAppliedBackendEntitiesRef.current = [];

    // 4. Rebuild form from remaining entities + surviving changes
    applyFormFromEntities(remainingEntities);
    scheduleFormValidation();

    logger.debug(TAG, 'merge form rebuilt', {
      masterLenAfter: remainingEntities.length,
      changedCountAfter: changesManager.getChangedEntitiesCount(),
      changedIds: changesManager.getChangedEntityIds(),
    });

    // 5. Notify dirty state while the Selected view is still active.
    onDirtyChange?.(changesManager.getChangedEntitiesCount() > 0);

    void (async () => {
      // 6. Invalidate validation report (matches delete handler step 6)
      await queryClient.invalidateQueries({ queryKey: [config.queryKeys.validationReport] });

      // 7. Refresh the ALL-tab cache before clearing selection. This prevents the
      //    SELECTED -> ALL transition from landing on the optimistic 5-row cache.
      const refreshedEntities = await refetchAfterMutation(
        loadedCountBefore,
        removedCount,
        mergedIds
      );

      if (refreshedEntities && refreshedEntities.length > 0) {
        const refreshedEntityIds = new Set(refreshedEntities.map(entity => entity.id));
        refreshedEntityIds.forEach(id => removedEntityIdsRef.current.delete(id));

        const nextEntities = refreshedEntities.filter(
          entity => !removedEntityIdsRef.current.has(entity.id)
        );

        masterEntityListRef.current = nextEntities;
        lastAppliedEntitiesRef.current = nextEntities;
        lastAppliedBackendEntitiesRef.current = refreshedEntities;
        applyFormFromEntities(nextEntities);
        scheduleFormValidation();
        onDirtyChange?.(changesManager.getChangedEntitiesCount() > 0);

        logger.debug(TAG, 'merge fresh data applied', {
          refreshedCount: refreshedEntities.length,
          masterLenAfterRefresh: nextEntities.length,
          removedIdsStillBlocked: Array.from(removedEntityIdsRef.current),
        });
      }

      // 8. Clean up UI state only after the ALL-tab cache is ready.
      table.resetRowSelection();

      logger.debug(TAG, 'merge cleanup done', {
        changedCountAfter: changesManager.getChangedEntitiesCount(),
        changedIdsAfter: changesManager.getChangedEntityIds(),
        refreshedCount: refreshedEntities?.length,
      });
    })();
  }, [
    table,
    changesManager,
    applyFormFromEntities,
    scheduleFormValidation,
    onDirtyChange,
    queryClient,
    refetchAfterMutation,
    selectedRows,
    TAG,
  ]);

  // --- Search result text ---
  let searchResultText: string;
  if (selectedRows.length > 0) {
    searchResultText = t(config.i18nKeys.searchResultSelected, {
      count: selectedRows.length,
    });
  } else if (!searchString) {
    searchResultText = t(config.i18nKeys.searchResultAll, {
      count: clientValidation.report?.total ?? 0,
    });
  } else {
    searchResultText = t(config.i18nKeys.searchResultCount, {
      count: totalEntities,
    });
  }

  const handleDeleteClick = () => {
    deleteModalProps.onOpen();
  };

  const handleRefetchAfterAddition = useCallback(
    async (addedCount: number) => {
      if (addedCount <= 0) return;

      const loadedCountBeforeAddition = masterEntityListRef.current.length;

      logger.debug(TAG, 'refetch after addition', {
        addedCount,
        loadedCountBeforeAddition,
      });

      await refetchAfterAddition(loadedCountBeforeAddition, addedCount);
    },
    [refetchAfterAddition, TAG]
  );

  const handleDeleteConfirm = () => {
    deleteMutation.mutate();
  };

  const hasBlockingErrors = (clientValidation.report?.errorsCount ?? 0) > 0;
  const hasBlockingDuplicates = (clientValidation.report?.duplicatesCount ?? 0) > 0;
  const hasSelectedRowErrors = selectedRows.some(row =>
    clientValidation.errorEntityIds.has(row.id)
  );
  const mergeDisabledTooltip = hasSelectedRowErrors
    ? t('authenticated.onboarding.common:duplicates.mergeDisabledWithErrors')
    : undefined;
  const deleteProtection = config.deleteProtection?.(selectedRows);
  const isDeleteDisabled = deleteMutation.isPending || (deleteProtection?.disabled ?? false);

  // --- Render action buttons ---
  const renderSelectionActions = () => (
    <>
      {permissions.canEdit &&
        permissions.canDelete &&
        config.renderResolveDuplicatesModal(selectedRowsWithFormValues, handleMergeSuccess, {
          disabled: hasSelectedRowErrors,
          disabledTooltip: mergeDisabledTooltip,
        })}
      {permissions.canDelete && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="outlineDestructive"
              size="sm"
              onClick={handleDeleteClick}
              disabled={isDeleteDisabled}
            >
              <Trash2 className="h-4 w-4" />
              {t(config.i18nKeys.deleteButton)}
            </Button>
          </TooltipTrigger>
          {deleteProtection?.tooltip ? (
            <TooltipPortal>
              <TooltipContent>
                <p>{deleteProtection.tooltip}</p>
              </TooltipContent>
            </TooltipPortal>
          ) : undefined}
        </Tooltip>
      )}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => table.resetRowSelection()}
        disabled={deleteMutation.isPending}
      >
        {t(config.i18nKeys.clearSelection)}
      </Button>
    </>
  );

  const renderAddActions = () => {
    if (!permissions.canCreate) return null;
    return (
      <>
        {config.renderAddButton(handleRefetchAfterAddition)}
        {config.renderImportButton(handleRefetchAfterAddition)}
      </>
    );
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(() => onSubmitValid())}
          className="flex min-h-0 flex-1 flex-col"
        >
          <div className="shrink-0">
            <div className="mb-4 flex flex-col gap-4">
              {(clientValidation.report?.total ?? 0) > 0 ? (
                <Alert variant="info">
                  <Info className="h-4 w-4" />
                  <AlertDescription className="text-xs/4 sm:text-sm">
                    {t('authenticated.onboarding.common:selectionActionsHint')}
                  </AlertDescription>
                </Alert>
              ) : null}
              {/* Mobile action buttons */}
              {selectedRows.length > 0 ? (
                <div className="flex flex-col items-start gap-2 sm:hidden">
                  {renderSelectionActions()}
                </div>
              ) : (
                <div className="flex flex-col items-start gap-2 sm:hidden">
                  {renderAddActions()}
                </div>
              )}

              <div className="flex items-center justify-between gap-6">
                <DataTableFilters
                  filtersConfig={getEntitiesOnboardingFiltersConfig(
                    config.i18nKeys.searchPlaceholder,
                    t,
                    {
                      ...clientValidation.report,
                      selectedCount: selectedRows.length,
                      resolvedFilters: {
                        [UiEntitiesFilter.ERRORS]:
                          (clientValidation.report?.errorsCount ?? 0) === 0 &&
                          uiEntitiesFilter === UiEntitiesFilter.ERRORS &&
                          pinnedFilterEntityIdsRef.current.size > 0,
                        [UiEntitiesFilter.DUPLICATES]:
                          (clientValidation.report?.duplicatesCount ?? 0) === 0 &&
                          uiEntitiesFilter === UiEntitiesFilter.DUPLICATES &&
                          pinnedFilterEntityIdsRef.current.size > 0,
                        [UiEntitiesFilter.WARNINGS]:
                          (clientValidation.report?.warningsCount ?? 0) === 0 &&
                          uiEntitiesFilter === UiEntitiesFilter.WARNINGS &&
                          pinnedFilterEntityIdsRef.current.size > 0,
                      },
                    }
                  )}
                  isClearButtonVisible={false}
                />
                {/* Desktop action buttons */}
                {selectedRows.length > 0 ? (
                  <div className="hidden flex-col gap-2 sm:flex lg:flex-row">
                    {renderSelectionActions()}
                  </div>
                ) : (
                  <div className="hidden flex-col gap-2 sm:flex lg:flex-row">
                    {renderAddActions()}
                  </div>
                )}
              </div>
            </div>
            <div className="my-4 text-sm text-muted-foreground">{searchResultText}</div>
          </div>

          {/* Scrollable Table Section */}
          <div
            className="flex min-h-0 flex-1 flex-col"
            onFocus={() => {
              isTableFocusedRef.current = true;
            }}
            onBlur={() => {
              isTableFocusedRef.current = false;
              // Re-check client-side counts on blur — navigate to "All" if all issues resolved.
              if (
                (uiEntitiesFilter === UiEntitiesFilter.ERRORS &&
                  (clientValidation.report?.errorsCount ?? 0) === 0) ||
                (uiEntitiesFilter === UiEntitiesFilter.DUPLICATES &&
                  (clientValidation.report?.duplicatesCount ?? 0) === 0) ||
                (uiEntitiesFilter === UiEntitiesFilter.WARNINGS &&
                  (clientValidation.report?.warningsCount ?? 0) === 0)
              ) {
                setFilterParam(EntitiesFilterFieldsKeys.PRESET, '');
              }
            }}
          >
            <EntityOnboardingTable
              fixedHeight={isInfiniteSizeTable}
              table={table}
              isLoading={isLoading || isFetchingNextPage}
              hasMore={hasNextPage}
              onLoadMore={handleLoadMore}
              error={error?.message}
              isAnyFilterApplied={
                (searchString && searchString.trim() !== '') || !!uiEntitiesFilter
              }
              totalCount={clientValidation.report?.total ?? 0}
              tableId={config.tableId}
              onResizeEnd={saveColumnSizing}
              fillLastColumns={config.integrationColumnsCount || 1}
              noDataYetKey={config.i18nKeys.noDataYet}
              noDataDescriptionKey={config.i18nKeys.noDataDescription}
              renderEmptyStateActions={() => <>{renderAddActions()}</>}
            />
          </div>

          {bottomSection({
            onClickSubmit: async () => {
              await form.handleSubmit(() => onSubmitValid())();
            },
            isLoading: updateMutation.isPending || submitMutation.isPending,
            isDisabled: hasBlockingErrors || hasBlockingDuplicates,
            nextButtonDisabledTooltip: hasBlockingErrors
              ? t(config.i18nKeys.submitEntitiesWithErrorsExist)
              : hasBlockingDuplicates
                ? t(config.i18nKeys.submitDuplicateEntitiesExists)
                : undefined,
          })}
        </form>
      </Form>

      <ConfirmDeleteEntityModal
        {...deleteModalProps}
        onConfirm={handleDeleteConfirm}
        selectedNames={selectedRowsWithFormValues.map(row => row.name)}
        isLoading={deleteMutation.isPending}
        i18nKeys={{
          title: config.i18nKeys.deleteModalTitle,
          text: config.i18nKeys.deleteModalText,
          cancel: config.i18nKeys.deleteModalCancel,
          confirm: config.i18nKeys.deleteModalConfirm,
          andMore: config.i18nKeys.deleteModalAndMore,
        }}
      />

      <ConfirmationModal
        title={t(config.i18nKeys.warningsModalTitle)}
        message={t(config.i18nKeys.warningsModalText)}
        confirmText={t(config.i18nKeys.warningsModalConfirm)}
        cancelText={t(config.i18nKeys.warningsModalCancel)}
        confirmVariant="default"
        open={warningsModalProps.open}
        onOpen={warningsModalProps.onOpen}
        onClose={warningsModalProps.onClose}
        onConfirm={closeModal => {
          submitIgnoringWarnings();
          closeModal();
        }}
      />
    </div>
  );
}
